import api from "@/config/api";
import { handleApiError } from "@/utils/ErrorHandler";
import { defaultPagination } from "@/store/common/pagination";

const state = {
    logs: [],
    priorityCounts: {},
    loading: false,
    error: null,
    pagination: defaultPagination(),
};

const mutations = {
    SET_LOGS(state, logs) {
        state.logs = logs;
    },
    SET_LOG_PRIORITY_COUNTS(state, counts) {
        state.priorityCounts = counts;
    },
    SET_LOADING(state, val) {
        state.loading = val;
    },
    SET_ERROR(state, err) {
        state.error = err;
    },
    SET_LOGS_PAGINATION(state, paginationData) {
        // Normalize pageSize to one of the allowed options if it's not already
        const allowedPageSizes = [5, 10, 25, 50, 100];
        let normalizedPageSize = paginationData.pageSize;
        if (!allowedPageSizes.includes(normalizedPageSize)) {
            // Find the closest larger option, or default to 10 if none larger
            normalizedPageSize = allowedPageSizes.find(size => size >= normalizedPageSize) || 10;
        }

        state.pagination = {
            ...state.pagination,
            ...paginationData,
            pageSize: normalizedPageSize,
        };
    },
};

const actions = {
    // ✅ Fetch logs with pagination and search
    async getLogs({ commit }, { page = 1, pageSize = 10, search = "", search_field = "all", start_date = "", end_date = "", exclude_identity = [], priorities = [] } = {}) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            // Build query parameters
            const params = new URLSearchParams();
            params.append('page_number', page);
            params.append('page_size', pageSize);
            if (search) {
                params.append('search', search);
            }
            if (search_field && search_field !== 'all') {
                params.append('search_field', search_field);
            }
            if (start_date) {
                params.append('start_date', start_date);
            }
            if (end_date) {
                params.append('end_date', end_date);
            }
            if (exclude_identity.length) {
                params.append('exclude_identity', exclude_identity.join(','));
            }
            if (priorities.length) {
                params.append('priorities', priorities.join(','));
            }

            const response = await api.get(`/logs?${params.toString()}`);

            // 🔑 Adjust depending on API shape
            const logs = response.data?.output || response.data?.data || response.data || [];
            const pagination = response.data?.pagination;
            const rawPriorityCounts = response.data?.priority_counts;
            const priorityCounts = {};
            if (Array.isArray(rawPriorityCounts)) {
                rawPriorityCounts.forEach(({ priority, count }) => {
                    const priorityValue = Number(priority);
                    if (Number.isInteger(priorityValue) && priorityValue >= 0 && priorityValue <= 7) {
                        priorityCounts[priorityValue] = Number(count) || 0;
                    }
                });
            } else if (rawPriorityCounts && typeof rawPriorityCounts === 'object') {
                for (let priority = 0; priority <= 7; priority++) {
                    const count = rawPriorityCounts[priority] ?? rawPriorityCounts[String(priority)];
                    if (count !== undefined && count !== null) {
                        priorityCounts[priority] = Number(count) || 0;
                    }
                }
            }

            // 🔑 Normalize logs keys to match table columns (camelCase → lowercase)
            const normalizedLogs = logs.map((log) => ({
                logid: log.logid ?? log.LogId,
                priority: log.priority ?? log.Priority,
                message: log.message ?? log.Message,
                timestamp: log.timestamp ?? log.TimeStamp,
                identity: log.identity ?? log.Identity,
                trackid: log.trackid ?? log.TrackId,
                pid: log.pid ?? log.ProcessId,
            }));

            commit("SET_LOGS", normalizedLogs);
            commit("SET_LOG_PRIORITY_COUNTS", priorityCounts);

            // Update pagination if provided
            if (pagination) {
                commit("SET_LOGS_PAGINATION", {
                    currentPage: pagination.current_page || page,
                    totalPages: pagination.total_pages || 1,
                    totalRecords: pagination.total_records || normalizedLogs.length,
                    pageSize: pagination.page_size || pageSize,
                    hasPrevious: pagination.has_previous || false,
                    hasNext: pagination.has_next || false,
                });
            } else {
                // If no pagination data, assume all data is loaded
                commit("SET_LOGS_PAGINATION", {
                    currentPage: page,
                    totalPages: 1,
                    totalRecords: normalizedLogs.length,
                    pageSize: pageSize,
                    hasPrevious: false,
                    hasNext: false,
                });
            }

            return normalizedLogs;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch logs");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

const getters = {
    allLogs: (state) => state.logs,
    logsLoading: (state) => state.loading,
    logsError: (state) => state.error,
    logsPagination: (state) => state.pagination,
    logsPriorityCounts: (state) => state.priorityCounts,
};

export default {
    state,
    mutations,
    actions,
    getters,
};
